import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { useAuth } from '../../hooks/useAuth';
import { isStaff } from '../../lib/roles';
import { normalizarBusqueda } from '../../lib/texto';
import { descargarCSV } from '../../lib/exportCsv';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import type { ProductoListItem, ProductoVentaItem } from '../../types/domain';

const COP = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

/**
 * Vista de staff (admin/recepción): catálogo completo, incluye
 * inactivos/agotados, costo implícito vía el módulo de inventario, y
 * acciones de crear/editar.
 *
 * "Descargar Excel" (pedido explícito del usuario, mismo botón que ya
 * existía en Inventario) exporta el catálogo completo ya cargado en
 * pantalla -- a diferencia de Inventario, esta consulta no tiene ningún
 * `.limit()` (trae todos los productos no anulados de una vez), así que
 * no hace falta una consulta aparte paginada para el archivo: lo que se
 * ve en la tabla es exactamente lo que se descarga. Incluye columnas que
 * la tabla no muestra (código, marca, costo, estado) para que el archivo
 * sirva como respaldo completo del catálogo, no solo de lo visible.
 */
function ProductosListStaff() {
  const { data, loading, error } = useSupabaseQuery<ProductoListItem[]>(async () => {
    const { data, error } = await supabase
      .from('productos')
      .select('*, categorias_productos ( nombre )')
      .neq('estado', 'anulado')
      .order('nombre', { ascending: true });

    if (error) return { data: null, error };

    const items = (data as unknown as Array<Record<string, unknown>>).map((row) => ({
      ...(row as unknown as ProductoListItem),
      categoria_nombre: (row.categorias_productos as { nombre: string } | null)?.nombre ?? 'Sin categoría',
    }));

    return { data: items, error: null };
  });

  function descargarExcel() {
    if (!data || data.length === 0) return;

    const filas = data.map((p) => [
      p.codigo,
      p.nombre,
      p.categoria_nombre,
      p.marca ?? '',
      p.costo,
      p.precio_venta,
      p.stock_minimo,
      p.stock_actual,
      p.estado,
    ]);

    descargarCSV(
      'Productos - catálogo',
      ['Código', 'Producto', 'Categoría', 'Marca', 'Costo', 'Precio de venta', 'Stock mínimo', 'Stock actual', 'Estado'],
      filas,
    );
  }

  return (
    <div>
      <PageHeader
        title="Productos"
        description="Catálogo de productos y su stock actual"
        actions={
          <div className="flex gap-2">
            {data && data.length > 0 && (
              <button type="button" className="btn-secondary" onClick={descargarExcel}>
                Descargar Excel
              </button>
            )}
            <Link to="/admin/productos/nuevo" className="btn-primary">
              Nuevo producto
            </Link>
          </div>
        }
      />

      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {data && data.length === 0 && (
        <EmptyState title="No hay productos registrados" description="Crea el primero desde el botón de arriba." />
      )}

      {data && data.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                <th className="px-4 py-3 font-medium">Producto</th>
                <th className="px-4 py-3 font-medium">Categoría</th>
                <th className="px-4 py-3 font-medium">Precio</th>
                <th className="px-4 py-3 font-medium">Stock</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.map((p) => {
                const bajo = p.stock_actual <= p.stock_minimo;
                const agotado = p.stock_actual <= 0;
                return (
                  <tr key={p.id} className="border-b border-onyx/5 last:border-0 hover:bg-onyx/[0.02]">
                    <td className="px-4 py-3 font-medium text-onyx">{p.nombre}</td>
                    <td className="px-4 py-3 text-onyx/70">{p.categoria_nombre}</td>
                    <td className="px-4 py-3 text-onyx/70">{COP.format(p.precio_venta)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`badge ${
                          agotado
                            ? 'bg-red-100 text-red-800'
                            : bajo
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {p.stock_actual} {agotado ? '· agotado' : bajo ? '· stock bajo' : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/admin/productos/${p.id}`} className="text-gold-deep hover:underline">
                        Editar
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Vista de profesional: solo lo que puede vender. Se lee a través de
 * `productos_disponibles_venta()` (security definer) en vez de la tabla
 * directa -- RLS filtra filas, no columnas, así que un acceso directo a
 * `productos` le mostraría costo y stock exacto aunque se restringiera a
 * "estado = activo and stock_actual > 0" desde el cliente. Sin costo, sin
 * stock, sin categoría de inventario, sin crear/editar. Incluye un
 * buscador por nombre (pedido explícito del usuario) que filtra en el
 * cliente sobre la lista ya cargada -- no hace falta volver a llamar al
 * RPC en cada tecla.
 */

function ProductosListProfesional() {
  const { data, loading, error } = useSupabaseQuery<ProductoVentaItem[]>(async () => {
    const { data, error } = await supabase.rpc('productos_disponibles_venta');
    if (error) return { data: null, error };
    const items = [...(data ?? [])].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return { data: items, error: null };
  });

  const [busqueda, setBusqueda] = useState('');
  // Filtro en el cliente: la lista que ve una profesional ya viene acotada
  // a lo que puede vender (productos activos con stock), así que no hace
  // falta ida y vuelta al servidor para buscar dentro de ese conjunto.
  const filtrados = useMemo(() => {
    if (!data) return data;
    const q = normalizarBusqueda(busqueda.trim());
    if (!q) return data;
    return data.filter((p) => normalizarBusqueda(p.nombre).includes(q));
  }, [data, busqueda]);

  return (
    <div>
      <PageHeader title="Productos" description="Productos disponibles para venta ahora mismo" />

      {data && data.length > 0 && (
        <div className="mb-4">
          <input
            type="text"
            className="input-field max-w-sm"
            placeholder="Buscar producto por nombre…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
      )}

      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {data && data.length === 0 && (
        <EmptyState
          title="No hay productos con stock disponible"
          description="Cuando haya productos activos con existencias, aparecerán aquí."
        />
      )}

      {data && data.length > 0 && filtrados && filtrados.length === 0 && (
        <EmptyState title="Sin resultados" description={`Ningún producto coincide con "${busqueda}".`} />
      )}

      {filtrados && filtrados.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                <th className="px-4 py-3 font-medium">Producto</th>
                <th className="px-4 py-3 font-medium">Precio</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((p) => (
                <tr key={p.id} className="border-b border-onyx/5 last:border-0 hover:bg-onyx/[0.02]">
                  <td className="px-4 py-3 font-medium text-onyx">{p.nombre}</td>
                  <td className="px-4 py-3 text-onyx/70">{COP.format(p.precio_venta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ProductosList() {
  const { perfil } = useAuth();
  return isStaff(perfil?.rol) ? <ProductosListStaff /> : <ProductosListProfesional />;
}
